/**
 * perfTelemetry.ts
 *
 * Lightweight, always-on main-thread timing instrumentation.
 *
 * Why this exists: the JS Self-Profiling API (`freezeProfiler.ts`) samples
 * the stack on a timer, but a TIGHT SYNCHRONOUS loop (e.g. an O(n^2) slot
 * assignment over hundreds of calendar days, or a big regex sweep over a
 * long caption) can run for tens of seconds between safepoints — the
 * profiler simply never gets scheduled to take a sample inside it, so a
 * freeze report shows a `longtaskMs` with NO stack at all.
 *
 * The fix here is explicit, cheap, `performance.now()`-based timing wrapped
 * around a short list of suspect synchronous operations (see call sites).
 * Anything >= SLOW_OP_THRESHOLD_MS is kept in a small ring buffer so that a
 * subsequent freeze report (see `telemetry.ts` `reportClientFreeze`) can
 * attach "here's what was slow recently" even when the self-profiler came
 * back empty.
 *
 * Every export here is fully guarded: it must never throw into the caller,
 * and it must never change the return value / control flow of the wrapped
 * operation. Pure observation only.
 */

/** Minimum duration (ms) for an operation to be recorded. */
const SLOW_OP_THRESHOLD_MS = 300;

/** Max number of recent slow ops retained (oldest dropped first). */
const MAX_ENTRIES = 24;

/** Cap on the serialized string handed to the freeze-report payload. */
const MAX_STRING_LENGTH = 1500;

export interface SlowOpEntry {
  label: string;
  ms: number;
  at: number;
}

// Module-level ring buffer — oldest-first internally; readers get newest-first.
const recentSlowOps: SlowOpEntry[] = [];

/**
 * Record a slow operation if it meets the threshold. Silently drops entries
 * below the threshold and silently swallows any unexpected error (this must
 * never be able to break the UI it's observing).
 */
export const recordSlowOp = (label: string, ms: number): void => {
  try {
    if (!(ms >= SLOW_OP_THRESHOLD_MS)) return;

    recentSlowOps.push({ label, ms: Math.round(ms), at: Date.now() });

    while (recentSlowOps.length > MAX_ENTRIES) {
      recentSlowOps.shift();
    }
  } catch {
    // Never throw from telemetry.
  }
};

/**
 * Time a synchronous function call and record it if slow. Measures even if
 * `fn` throws (try/finally), then rethrows the original error unchanged —
 * this wrapper must be a no-op with respect to behavior/control-flow.
 */
export const timeSync = <T>(label: string, fn: () => T): T => {
  const t0 = performance.now();
  try {
    return fn();
  } finally {
    try {
      recordSlowOp(label, performance.now() - t0);
    } catch {
      // Never throw from telemetry.
    }
  }
};

/**
 * Return the recent slow ops, NEWEST-FIRST, as a compact
 * `"label:ms,label:ms,..."` string capped to ~MAX_STRING_LENGTH chars.
 * Returns an empty string when there are none, or on any failure.
 */
export const getRecentSlowOpsString = (): string => {
  try {
    if (recentSlowOps.length === 0) return '';

    const newestFirst = [...recentSlowOps].reverse();
    const parts: string[] = [];
    let length = 0;

    for (const entry of newestFirst) {
      const part = `${entry.label}:${entry.ms}`;
      // +1 accounts for the joining comma (except before the first part).
      const addedLength = parts.length === 0 ? part.length : part.length + 1;
      if (length + addedLength > MAX_STRING_LENGTH) break;
      parts.push(part);
      length += addedLength;
    }

    return parts.join(',');
  } catch {
    return '';
  }
};
